import React, { Fragment, FunctionComponent, useCallback, useEffect, useRef, useState } from "react";
import styles from "./MediaCloudOverlay.module.scss";
import MediaCloudOverlayContent from "./MediaCloudOverlay.interface";

import { AppRootState } from "app/redux.store";
import { CLOSE_ALL_OVERLAYS } from "layout/redux/overlay.actions";
import { useDispatch, useSelector } from "react-redux";
import CopyToClipboard from "react-copy-to-clipboard";
import Dictionary from "components/Dictionary/Dictionary";
import IconViewer from "components/IconViewer/IconViewer";
import MediaCloudItemContent from "../MediaCloudItem/MediaCloudItem.interface";
import sendMediaCloudEmail from "./helpers/sendMediaCloudEmail";
import Spinner from "components/Spinner/Spinner";
import urlBuilder from "app/umbraco/helpers/urlBuilder";
import useClickOutside from "./hooks/useClickOutside";
import useTranslation from "components/Dictionary/hooks/useTranslation";

const MediaCloudOverlay: FunctionComponent<MediaCloudOverlayContent> = (props) => {
    const dispatch = useDispatch();
    const translations = useTranslation("MEDIA_CLOUD.SENDER_EMAIL", "MEDIA_CLOUD.RECIPIENT_EMAIL") as Record<string, string>;
    const overlay = useSelector<AppRootState, { content: MediaCloudItemContent }>((state) => state.overlay);

    const isComponentMounted = useRef(false);

    const [formState, setFormState] = useState({});
    const [viewState, setViewState] = useState("INIT");
    const [isCopiedToClipboard, setIsCopiedToClipboard] = useState(false);

    useEffect(() => {
        isComponentMounted.current = true;
        return () => {
            isComponentMounted.current = false;
        };
    }, []);

    const closeOverlay = useCallback(() => {
        dispatch({
            type: CLOSE_ALL_OVERLAYS,
        });
    }, [dispatch]);

    const onEscKeyInput = useCallback((event: any) => {
        if (event.keyCode === 27) {
            dispatch({
                type: CLOSE_ALL_OVERLAYS,
            });
        }
    }, []);

    const setFileUrl = (url: string, resolution: "ZIP" | "LOW" | "HIGH"): string => {
        const result = urlBuilder(url);
        const params = new URLSearchParams();

        if (resolution === "HIGH") {
            params.set("upscale", "false");
            params.set("width", "7680"); // 8K
        } else if (resolution === "LOW") {
            params.set("upscale", "false");
            params.set("width", "852"); // 480p
        }

        if (resolution === "ZIP") {
            return result.toString();
        } else {
            const querySeparator = result.toString().indexOf("?") > 0 ? "&" : "?";
            return `${result}${querySeparator}${params.toString()}`;
        }
    };

    const onFormChange = (event: any) => {
        event.preventDefault();

        const form = JSON.parse(JSON.stringify(formState));
        form[event.target.name] = event.target.value;
        setFormState(form);
    };

    const onFormSubmit = (event: any) => {
        event.preventDefault();

        const form = JSON.parse(JSON.stringify(formState));
        form["DownloadUrl"] = setFileUrl(overlay.content.url, overlay.content.url.indexOf("getzip") > 0 ? "ZIP" : "HIGH");

        setViewState("LOADING");

        sendMediaCloudEmail(form)
            .then(() => {
                if (isComponentMounted?.current === true) {
                    setViewState("SUCCESS");
                }
            })
            .catch(() => {
                if (isComponentMounted?.current === true) {
                    setViewState("ERROR");
                }
            });
    };

    useEffect(() => {
        document.addEventListener("keydown", onEscKeyInput, false);

        return () => {
            document.removeEventListener("keydown", onEscKeyInput, false);
        };
    }, []);

    const innerElementRef = useClickOutside(closeOverlay);

    return (
        <div className={styles.wrapper}>
            <div
                className={styles.inner}
                ref={innerElementRef}
            >
                <button
                    className={styles.close_button}
                    onClick={() => closeOverlay()}
                >
                    <IconViewer
                        className={styles.close_icon}
                        name="close"
                    />
                </button>

                <div className={styles.content}>
                    {overlay.content.product ? (
                        <Fragment>
                            <span className={styles.tagline}>{overlay.content.product.sku}</span>
                            <h3 className={styles.headline}>{overlay.content.product.itemName}</h3>
                        </Fragment>
                    ) : null}

                    {!overlay.content.product ? (
                        <h3 className={styles.headline}>
                            <Dictionary name="MEDIA_CLOUD.SEND_LINK_HEADLINE" />
                        </h3>
                    ) : null}

                    {/* Download */}
                    {props.view === "DOWNLOAD" ? (
                        <Fragment>
                            <span>
                                <Dictionary name="MEDIA_CLOUD.CHOOSE_RESOLUTION" />
                            </span>

                            <a
                                className={styles.link}
                                href={setFileUrl("media" + overlay.content.url, "LOW")}
                                download
                            >
                                <IconViewer
                                    className={styles.link_icon}
                                    name="download"
                                />
                                <Dictionary name="MEDIA_CLOUD.DOWNLOAD_LOW_RESOLUTION" />
                            </a>

                            <a
                                className={styles.link}
                                href={setFileUrl("media" + overlay.content.url, "HIGH")}
                                download
                            >
                                <IconViewer
                                    className={styles.link_icon}
                                    name="download"
                                />
                                <Dictionary name="MEDIA_CLOUD.DOWNLOAD_HIGH_RESOLUTION" />
                            </a>
                        </Fragment>
                    ) : null}

                    {/* Email */}
                    {props.view === "EMAIL" && viewState === "INIT" ? (
                        <Fragment>
                            {/* <CopyToClipboard
                                text={setFileUrl("media" + overlay.content.url, overlay.content.url.indexOf("getzip") > 0 ? "ZIP" : "HIGH")}
                                onCopy={() => setIsCopiedToClipboard(true)}
                            >
                                <button className={styles.copy_button}>
                                    <IconViewer
                                        className={styles.copy_icon}
                                        name="download"
                                    />
                                    <Dictionary name={isCopiedToClipboard === true ? "MEDIA_CLOUD.COPIED_DOWNLOAD_LINK" : "MEDIA_CLOUD.COPY_DOWNLOAD_LINK"} />
                                </button>
                            </CopyToClipboard> */}

                            <button
                                className={styles.copy_button}
                                onClick={() => {
                                    navigator.clipboard
                                        .writeText(setFileUrl("media" + overlay.content.url, overlay.content.url.indexOf("getzip") > 0 ? "ZIP" : "HIGH"))
                                        .then(() => setIsCopiedToClipboard(true));
                                }}
                            >
                                {/* TODO: change icon to chainlink icon */}
                                <IconViewer
                                    className={styles.copy_icon}
                                    name="download"
                                />
                                <Dictionary name={isCopiedToClipboard === true ? "MEDIA_CLOUD.COPIED_DOWNLOAD_LINK" : "MEDIA_CLOUD.COPY_DOWNLOAD_LINK"} />
                            </button>

                            <form
                                className={styles.form}
                                onSubmit={(event) => onFormSubmit(event)}
                            >
                                <input
                                    type="email"
                                    required
                                    autoComplete="off"
                                    placeholder={translations["MEDIA_CLOUD.SENDER_EMAIL"]}
                                    name="FromEmail"
                                    onChange={(event) => onFormChange(event)}
                                />
                                <input
                                    type="email"
                                    required
                                    autoComplete="off"
                                    placeholder={translations["MEDIA_CLOUD.RECIPIENT_EMAIL"]}
                                    name="ToEmail"
                                    onChange={(event) => onFormChange(event)}
                                />
                                <button type="submit">
                                    <span className={styles.button_label}>
                                        <Dictionary name="MEDIA_CLOUD.SEND_EMAIL" />
                                    </span>
                                </button>
                            </form>
                        </Fragment>
                    ) : null}

                    {/* Email: Loading */}
                    {props.view === "EMAIL" && viewState === "LOADING" ? (
                        <div className={styles.loading}>
                            <Spinner className={styles.loading_spinner} />
                            <Dictionary name="MEDIA_CLOUD.SENDING" />
                        </div>
                    ) : null}

                    {/* Email: Success */}
                    {props.view === "EMAIL" && viewState === "SUCCESS" ? (
                        <div className={styles.success}>
                            <Dictionary name="MEDIA_CLOUD.SENT_SUCCESS" />
                        </div>
                    ) : null}

                    {/* Email: Error */}
                    {props.view === "EMAIL" && viewState === "ERROR" ? (
                        <div className={styles.error}>
                            <Dictionary name="MEDIA_CLOUD.SENT_ERROR" />
                        </div>
                    ) : null}
                </div>
            </div>
        </div>
    );
};

export default MediaCloudOverlay;
